import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { contactAPI } from '../services/contactAPI';
import '../styles/Home.css';

const Home = () => {
  const { user } = useAuth();
  
  // Estado para el formulario de contacto
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  // Función para manejar cambios en el formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Función para enviar el formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      // Enviar formulario usando la API real
      const response = await contactAPI.submitContactForm(contactForm);
      
      if (response.success) {
        setSubmitMessage(response.message);
        setContactForm({
          name: '',
          email: '',
          company: '',
          message: '',
          phone: ''
        });
      } else {
        setSubmitMessage('Error al enviar el mensaje. Por favor, intenta nuevamente.');
      }
    } catch (error) {
      // Manejar diferentes tipos de errores
      if (error.message) {
        setSubmitMessage(error.message);
      } else {
        setSubmitMessage('Error al enviar el mensaje. Por favor, verifica tu conexión e intenta nuevamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Effect para animaciones de scroll
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
        }
      });
    }, observerOptions);

    // Observar elementos con la clase 'scroll-animate'
    const animateElements = document.querySelectorAll('.scroll-animate');
    animateElements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-vh-100 bg-light">
      {/* Navbar */}
      <nav className="navbar navbar-expand-lg navbar-dark bg-primary">
        <div className="container">
          <Link className="navbar-brand fw-bold" to="/">
            <i className="bi bi-file-pdf me-2"></i>
            DataExtractor
          </Link>
          
          <button 
            className="navbar-toggler" 
            type="button" 
            data-bs-toggle="collapse" 
            data-bs-target="#navbarNav"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          
          <div className="collapse navbar-collapse" id="navbarNav">
            <ul className="navbar-nav ms-auto">
              <li className="nav-item">
                <a className="nav-link" href="#servicios">Servicios</a>
              </li>
              <li className="nav-item">
                <a className="nav-link" href="#precios">Precios</a>
              </li>
              <li className="nav-item">
                <a className="nav-link" href="#contacto">Contacto</a>
              </li>
              {/* Mostrar acceso rápido si el usuario está autenticado */}
              {user ? (
                <li className="nav-item ms-2">
                  <Link className="btn btn-light btn-sm fw-bold" to="/extractor">
                    <i className="bi bi-lightning-charge me-2"></i>
                    Ir al Panel
                  </Link>
                </li>
              ) : (
                <>
                  <li className="nav-item ms-2">
                    <Link className="btn btn-outline-light btn-sm" to="/login">
                      Iniciar Sesión
                    </Link>
                  </li>
                  <li className="nav-item ms-2">
                    <Link className="btn btn-light btn-sm" to="/register">
                      Registrarse
                    </Link>
                  </li>
                </>
              )}
            </ul>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-primary text-white py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h1 className="display-4 fw-bold mb-4">
                Extrae Datos Clave de Cualquier Documento
              </h1>
              <p className="lead mb-4">
                ¡Convierte tus documentos digitalizados en datos útiles con IA avanzada!
                Extrae información de PDFs, facturas, contratos y documentos legales en segundos, sin esfuerzo.
                Personaliza cada extracción, automatiza tu flujo de trabajo y elimina tareas manuales. ¡Hazlo fácil, hazlo inteligente!
              </p>
              <div className="d-flex gap-3">
                {/* Si el usuario está autenticado, mostrar acceso rápido */}
                {user ? (
                  <Link to="/extractor" className="btn btn-light btn-lg">
                    <i className="bi bi-lightning-charge me-2"></i>
                    Ir al Panel
                  </Link>
                ) : (
                    <Link to="/register" className="btn btn-light btn-lg">
                      <i className="bi bi-rocket-takeoff me-2"></i>
                      Comenzar Gratis
                    </Link>
                )}
                <a href="#demo" className="btn btn-outline-light btn-lg">
                      <i className="bi bi-play-circle me-2"></i>
                      Ver Demo
                    </a>
              </div>
            </div>
            <div className="col-lg-6 text-center">
              <div className="bg-white bg-opacity-10 rounded-3 p-4 hero-animation">
                <i className="bi bi-file-pdf display-1 text-white"></i>
                <div className="mt-3">
                  <i className="bi bi-arrow-down text-white display-6"></i>
                </div>
                <i className="bi bi-table display-1 text-white mt-3"></i>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="servicios" className="py-5 scroll-animate">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-5 fw-bold">¿Cómo Funciona?</h2>
            <p className="lead text-muted">
              Proceso simple y automatizado en 4 pasos
            </p>
          </div>
          
          <div className="row g-4">
            <div className="col-md-3 scroll-animate">
              <div className="card border-0 shadow-sm h-100 text-center card-hover">
                <div className="card-body">
                  <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3 feature-icon" 
                       style={{ width: '80px', height: '80px' }}>
                    <i className="bi bi-gear text-primary display-6"></i>
                  </div>
                  <h5 className="card-title">1. Personaliza tu extracción</h5>
                  <p className="card-text text-muted">
                    Elige qué datos necesitas y crea plantillas a medida, o usa nuestras opciones predefinidas para empezar rápido.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="col-md-3 scroll-animate">
              <div className="card border-0 shadow-sm h-100 text-center card-hover">
                <div className="card-body">
                  <div className="bg-success bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3 feature-icon" 
                       style={{ width: '80px', height: '80px' }}>
                    <i className="bi bi-cloud-upload text-success display-6"></i>
                  </div>
                  <h5 className="card-title">2. Sube tus documentos</h5>
                  <p className="card-text text-muted">
                    Arrastra y suelta tus archivos PDF, JPG, PNG o ZIP, o selecciónalos desde tu dispositivo. Sin límites de cantidad ni tamaño.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="col-md-3 scroll-animate">
              <div className="card border-0 shadow-sm h-100 text-center card-hover">
                <div className="card-body">
                  <div className="bg-warning bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3 feature-icon" 
                       style={{ width: '80px', height: '80px' }}>
                    <i className="bi bi-cpu text-warning display-6"></i>
                  </div>
                  <h5 className="card-title">3. Deja que la IA trabaje</h5>
                  <p className="card-text text-muted">
                    Nuestra inteligencia artificial analiza y extrae los datos automáticamente, adaptándose a cada tipo de documento y plantilla.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="col-md-3 scroll-animate">
              <div className="card border-0 shadow-sm h-100 text-center card-hover">
                <div className="card-body">
                  <div className="bg-info bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3 feature-icon" 
                       style={{ width: '80px', height: '80px' }}>
                    <i className="bi bi-download text-info display-6"></i>
                  </div>
                  <h5 className="card-title">4. Descarga y utiliza tus datos</h5>
                  <p className="card-text text-muted">
                    Exporta los resultados en Excel, CSV o JSON y utilízalos de inmediato en tus sistemas, reportes o análisis.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Video Section Mejorada */}
      <section id="demo" className="py-5 bg-light scroll-animate">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 scroll-animate">
              <h2 className="display-6 fw-bold mb-4">Ve DataExtractor en Acción</h2>
              <p className="lead mb-3">
                Convierte documentos escaneados en datos listos para usar.
                Sube archivos en PDF, JPG, PNG o ZIP y extrae información clave de oficios, resoluciones, contratos y más — todo en segundos, con solo unos clics.
              </p>
              <ul className="list-unstyled mb-4 ">
                <li className="mb-2">
                  <i className="bi bi-check-circle-fill text-success me-2"></i>
                  Elige el método de extracción que prefieras, rápido para documentos simples o preciso para casos complejos.
                </li>
                <li className="mb-2">
                  <i className="bi bi-check-circle-fill text-success me-2"></i>
                  Consulta y descarga tu historial visual de extracciones por lote, incluso si el proceso se interrumpe.
                </li>
                <li className="mb-2">
                  <i className="bi bi-check-circle-fill text-success me-2"></i>
                  Recibe feedback claro por cada archivo: sabrás al instante si fue exitoso o si hubo algún error.
                </li>
                <li className="mb-2">
                  <i className="bi bi-check-circle-fill text-success me-2"></i>
                  Visualiza y exporta tus resultados paginados a Excel, CSV o JSON, listos para usar.
                </li>
                <li className="mb-2">
                  <i className="bi bi-check-circle-fill text-success me-2"></i>
                  Aprovecha precios flexibles y paga solo por lo que usas, con descuentos por volumen.
                </li>
              </ul>
              <div className="alert alert-info py-2 px-3 mb-2">
                <i className="bi bi-info-circle me-1"></i>
                <strong>¿Quieres ver cómo funciona?</strong> Prueba la demo o regístrate gratis para experimentar la extracción inteligente de datos.
              </div>
            </div>
            <div className="col-lg-6">
              <div className="position-relative">
                <div className="ratio ratio-16x9 rounded-3 overflow-hidden shadow-lg demo-video-placeholder">
                  <div className="bg-dark d-flex align-items-center justify-content-center">
                    <button className="btn btn-light btn-lg rounded-circle demo-play-button" style={{ width: '80px', height: '80px' }}>
                      <i className="bi bi-play-fill display-6"></i>
                    </button>
                  </div>
                </div>
                <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center">
                  <span className="text-white fs-5">Video Demo (Próximamente)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="precios" className="py-5 scroll-animate">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-5 fw-bold">Precios Simples y Transparentes</h2>
            <p className="lead text-muted">
              Compra documentos solo cuando los necesites, sin suscripciones ni vencimientos. 
              <br />Mientras más compres, más ahorras con nuestros descuentos por volumen.
            </p>
          </div>
          
          <div className="row g-4 justify-content-center">
            {/* Plan Gratuito */}
            <div className="col-lg-3 col-md-6 scroll-animate">
              <div className="card border-0 shadow-sm h-100 card-hover pricing-card">
                <div className="card-header bg-white text-center py-4">
                  <h5 className="card-title">Cuenta Gratuita</h5>
                  <div className="display-4 fw-bold text-success">Gratis</div>
                  <small className="text-muted">para empezar</small>
                </div>
                <div className="card-body">
                  <ul className="list-unstyled">
                    <li className="mb-3">
                      <i className="bi bi-check text-success me-2"></i>
                      <strong>10 documentos gratis</strong>
                    </li>
                    <li className="mb-3">
                      <i className="bi bi-check text-success me-2"></i>
                      Plantillas básicas predefinidas
                    </li>
                    <li className="mb-3">
                      <i className="bi bi-check text-success me-2"></i>
                      Configuración de campos personalizados
                    </li>
                    <li className="mb-3">
                      <i className="bi bi-check text-success me-2"></i>
                      Exportar a Excel/CSV
                    </li>
                    <li className="mb-3">
                      <i className="bi bi-check text-success me-2"></i>
                      Soporte por email
                    </li>
                  </ul>
                </div>
                <div className="card-footer bg-white">
                  <Link to="/register" className="btn btn-success w-100 btn-animation">
                    Comenzar Gratis
                  </Link>
                </div>
              </div>
            </div>

            {/* Paquete Básico */}
            <div className="col-lg-3 col-md-6 scroll-animate">
              <div className="card border-0 shadow h-100 card-hover pricing-card">
                <div className="card-header bg-white text-center py-4">
                  <h5 className="card-title">Paquete Básico</h5>
                  <div className="display-4 fw-bold text-primary">S/ 5</div>
                  <small className="text-muted">por 200 documentos</small>
                
                </div>
                <div className="card-body">
                  <ul className="list-unstyled">
                    <li className="mb-3">
                      <i className="bi bi-check text-success me-2"></i>
                      <strong>200 documentos digitalizados</strong>
                    </li>
                    <li className="mb-3">
                      <i className="bi bi-check text-success me-2"></i>
                      Extracción avanzada con IA
                    </li>
                    <li className="mb-3">
                      <i className="bi bi-check text-success me-2"></i>
                      Todos los formatos de exportación
                    </li>
                    <li className="mb-3">
                      <i className="bi bi-check text-success me-2"></i>
                      Historial de extracciones
                    </li>
                    <li className="mb-3">
                      <i className="bi bi-check text-success me-2"></i>
                      Soporte prioritario
                    </li>
                  </ul>
                </div>
                <div className="card-footer bg-white">
                  <Link to="/register" className="btn btn-primary w-100 btn-animation">
                    Comprar Básico
                  </Link>
                </div>
              </div>
            </div>

            {/* Paquete Popular */}
            <div className="col-lg-3 col-md-6 scroll-animate">
              <div className="card border-primary shadow-lg h-100 card-hover pricing-card">
                <div className="card-header bg-primary text-white text-center py-4">
                  <h5 className="card-title">Paquete Intermedio</h5>
                  <div className="display-4 fw-bold">S/ 12</div>
                  <small>por 500 documentos</small>
                  <div className="mt-2">
                    <span className="badge bg-success fs-6 px-2 py-1">
                      ¡Ahorra 5%!
                    </span>
                  </div>
                </div>
                <div className="card-body">
                  <ul className="list-unstyled">
                    <li className="mb-3">
                      <i className="bi bi-check text-success me-2"></i>
                      <strong>500 documentos digitalizados</strong>
                    </li>
                    <li className="mb-3">
                      <i className="bi bi-check text-success me-2"></i>
                      <strong>5% de descuento incluido</strong>
                    </li>
                    <li className="mb-3">
                      <i className="bi bi-check text-success me-2"></i>
                      Extracción avanzada con IA
                    </li>
                    <li className="mb-3">
                      <i className="bi bi-check text-success me-2"></i>
                      Todos los formatos de exportación
                    </li>
                    <li className="mb-3">
                      <i className="bi bi-check text-success me-2"></i>
                      Soporte prioritario 24/7
                    </li>
                  </ul>
                </div>
                <div className="card-footer bg-white">
                  <Link to="/register" className="btn btn-primary w-100 btn-animation">
                    Comprar Intermedio
                  </Link>
                </div>
              </div>
            </div>

            {/* Paquete Empresarial */}
            <div className="col-lg-3 col-md-6 scroll-animate">
              <div className="card border-0 shadow-sm h-100 card-hover pricing-card">
                <div className="card-header bg-white text-center py-4">
                  <h5 className="card-title">Volumen Empresarial</h5>
                  <div className="display-4 fw-bold text-warning">S/ 64</div>
                  <small className="text-muted">por 3000 documentos</small>
                  <div className="mt-2">
                    <span className="badge bg-success fs-6 px-2 py-1">
                      ¡Ahorra hasta 15%!
                    </span>
                  </div>
                </div>
                <div className="card-body">
                  <ul className="list-unstyled">
                    <li className="mb-3">
                      <i className="bi bi-check text-success me-2"></i>
                      <strong>3000 documentos digitalizados</strong>
                    </li>
                    <li className="mb-3">
                      <i className="bi bi-check text-success me-2"></i>
                      <strong>15% de descuento máximo</strong>
                    </li>
                    <li className="mb-3">
                      <i className="bi bi-check text-success me-2"></i>
                      IA personalizada avanzada
                    </li>
                    <li className="mb-3">
                      <i className="bi bi-check text-success me-2"></i>
                      Soporte dedicado 24/7
                    </li>
                    <li className="mb-3">
                      <i className="bi bi-check text-success me-2"></i>
                      Reportes avanzados y analytics
                    </li>
                  </ul>
                </div>
                <div className="card-footer bg-white">
                  <Link to="/register" className="btn btn-warning w-100 btn-animation">
                    Comprar Empresarial
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Información adicional de precios */}
          <div className="row mt-5">
            <div className="col-12">
              <div className="card border-0 bg-light">
                <div className="card-body text-center py-4">
                  <h5 className="fw-bold mb-3">
                    <i className="bi bi-calculator me-2"></i>
                    Precios Flexibles y Escalables
                  </h5>
                  <div className="row g-4">
                    <div className="col-md-3">
                      <div className="text-primary">
                        <i className="bi bi-arrow-up-circle display-6"></i>
                      </div>
                      <h6 className="mt-2">Cantidad Flexible</h6>
                      <p className="text-muted small mb-0">
                        Desde 100 hasta 5,000 documentos<br />
                        en incrementos de 100
                      </p>
                    </div>
                    <div className="col-md-3">
                      <div className="text-success">
                        <i className="bi bi-percent display-6"></i>
                      </div>
                      <h6 className="mt-2">Descuentos Automáticos</h6>
                      <p className="text-muted small mb-0">
                        5% (500+), 10% (1500+)<br />
                        hasta 15% (3000+)
                      </p>
                    </div>
                    <div className="col-md-3">
                      <div className="text-info">
                        <i className="bi bi-infinity display-6"></i>
                      </div>
                      <h6 className="mt-2">Sin Vencimiento</h6>
                      <p className="text-muted small mb-0">
                        Tus documentos no caducan<br />
                        úsalos cuando quieras
                      </p>
                    </div>
                    <div className="col-md-3">
                      <div className="text-warning">
                        <i className="bi bi-credit-card display-6"></i>
                      </div>
                      <h6 className="mt-2">Pago Simple</h6>
                      <p className="text-muted small mb-0">
                        Solo por Yape por ahora<br />
                        Más métodos pronto
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 d-flex flex-column flex-md-row gap-3 justify-content-center">
                    <Link to="/comprar-documentos" className="btn btn-primary btn-lg btn-animation">
                      <i className="bi bi-calculator me-2"></i>
                      Calcular Precio Exacto
                    </Link>
                    <Link to="/register" className="btn btn-outline-primary btn-lg btn-animation">
                      <i className="bi bi-person-plus me-2"></i>
                      Comenzar Gratis
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="py-5 contact-form scroll-animate">
        <div className="container">
          <div className="row">
            <div className="col-lg-6 text-white">
              <h2 className="display-6 fw-bold mb-4">¿Listo para Comenzar?</h2>
              <p className="lead mb-4">
                Descubre la nueva forma de automatizar la extracción de datos 
                de tus documentos PDF con inteligencia artificial avanzada.
              </p>
              
              {/* Estadísticas */}
              <div className="row g-3 mb-4">
                <div className="col-6">
                  <div className="text-center stat-counter">
                    <div className="display-4 fw-bold">99.5%</div>
                    <small>Precisión</small>
                  </div>
                </div>
                <div className="col-6">
                  <div className="text-center stat-counter">
                    <div className="display-4 fw-bold">Beta</div>
                    <small>Versión Actual</small>
                  </div>
                </div>
                <div className="col-6">
                  <div className="text-center stat-counter">
                    <div className="display-4 fw-bold">10</div>
                    <small>Docs Gratis</small>
                  </div>
                </div>
                <div className="col-6">
                  <div className="text-center stat-counter">
                    <div className="display-4 fw-bold">24/7</div>
                    <small>Soporte</small>
                  </div>
                </div>
              </div>
              
              <div className="d-flex gap-3">
                <Link to="/register" className="btn btn-light btn-lg btn-animation">
                  <i className="bi bi-rocket-takeoff me-2"></i>
                  Comenzar Ahora
                </Link>
                <a href="mailto:hsanchez@titanius.pe" className="btn btn-outline-light btn-lg btn-animation">
                  <i className="bi bi-envelope me-2"></i>
                  Email Directo
                </a>
              </div>
            </div>
            
            {/* Formulario de Contacto */}
            <div className="col-lg-6">
              <div className="card border-0 shadow-lg">
                <div className="card-header bg-white text-center py-4">
                  <h4 className="fw-bold text-primary mb-0">
                    <i className="bi bi-envelope-paper me-2"></i>
                    Contáctanos
                  </h4>
                  <small className="text-muted">Te responderemos en menos de 24 horas</small>
                </div>
                <div className="card-body p-4">
                  {submitMessage && (
                    <div className={`alert ${submitMessage.includes('Error') ? 'alert-danger' : 'alert-success'} alert-dismissible fade show`}>
                      <i className={`bi ${submitMessage.includes('Error') ? 'bi-exclamation-triangle' : 'bi-check-circle'} me-2`}></i>
                      {submitMessage}
                    </div>
                  )}
                  
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-floating">
                          <input
                            type="text"
                            className="form-control"
                            id="name"
                            name="name"
                            value={contactForm.name}
                            onChange={handleInputChange}
                            placeholder="Tu nombre completo"
                            required
                          />
                          <label htmlFor="name">
                            <i className="bi bi-person me-1"></i>
                            Nombre completo *
                          </label>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-floating">
                          <input
                            type="email"
                            className="form-control"
                            id="email"
                            name="email"
                            value={contactForm.email}
                            onChange={handleInputChange}
                            placeholder="tu@email.com"
                            required
                          />
                          <label htmlFor="email">
                            <i className="bi bi-envelope me-1"></i>
                            Email *
                          </label>
                        </div>
                      </div>
                    </div>
                    
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-floating">
                          <input
                            type="text"
                            className="form-control"
                            id="company"
                            name="company"
                            value={contactForm.company}
                            onChange={handleInputChange}
                            placeholder="Tu empresa"
                          />
                          <label htmlFor="company">
                            <i className="bi bi-building me-1"></i>
                            Empresa
                          </label>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-floating">
                          <input
                            type="tel"
                            className="form-control"
                            id="phone"
                            name="phone"
                            value={contactForm.phone}
                            onChange={handleInputChange}
                            placeholder="+51 999 999 999"
                          />
                          <label htmlFor="phone">
                            <i className="bi bi-telephone me-1"></i>
                            Teléfono
                          </label>
                        </div>
                      </div>
                    </div>
                    
                    <div className="form-floating">
                      <textarea
                        className="form-control"
                        id="message"
                        name="message"
                        value={contactForm.message}
                        onChange={handleInputChange}
                        placeholder="Cuéntanos sobre tu proyecto..."
                        style={{ height: '120px' }}
                        required
                      ></textarea>
                      <label htmlFor="message">
                        <i className="bi bi-chat-text me-1"></i>
                        Mensaje *
                      </label>
                    </div>
                    
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn btn-lg w-100 text-white submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Enviando...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-send me-2"></i>
                          Enviar Mensaje
                        </>
                      )}
                    </button>
                    
                    <div className="text-center mt-3">
                      <small className="text-muted">
                        <i className="bi bi-shield-check me-1"></i>
                        Tus datos están protegidos y no serán compartidos
                      </small>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-dark text-white py-4">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-md-6">
              <div className="d-flex align-items-center">
                 <a 
                    href="https://titanius.pe/" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="text-white text-decoration-none"
                  >
                    <strong>Titanius Consulting S.R.L.</strong>
                </a>
                <span className="ms-2 text-muted">© 2025 Todos los derechos reservados</span>
              </div>
            </div>
            <div className="col-md-6 text-md-end">
              <a href="#contacto" className="text-white">Contacto</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
